import { useEffect, useState, useRef } from "react";
import { auth } from "../../../firebase"
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../../../components/button/Button";
import userQuery from "../../../firestore/User.query";
import pointDataQuery from "../../../firestore/PointData.query";
import { PointHistoryData, pointDataTimestampToDate } from "../../../model/PointData";
import { convertDateTimeToJapaneseStringHalfwidth, convertDateTimeToJapaneseStringMonth } from "../../../utility/ConvertUtily";
import { Spiner } from "../../../components/spinner/Spiner";
import { alertOpen, AlertValue } from "../../../features/alert.slice";
import { useDispatch } from "react-redux";
import { ConstMessage } from "../../../config/const";
import { DocumentData, QueryDocumentSnapshot } from "firebase/firestore";
import { SelectFields } from "../../../components/select/SelectFields";

export const PointHistoryListMobile = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ログイン中のユーザ情報
  const currentUser = auth.currentUser;

  const [isLoad, setIsLoad] = useState(true);
  const [isGrant, setIsGrant] = useState<boolean>(true);
  const [rowList, setRowList] = useState<any[]>([]);
  const [ownedPoint, setOwnedPoint] = useState<number>(0);
  const [pointHistoryGrantLength, setPointHistoryGrantLength] = useState<number>(0);
  const [pointHistoryUsageLength, setPointHistoryUsageLength] = useState<number>(0);
  const [pointHistoryList, setPointHistoryList] = useState<PointHistoryData[]>([]);
  const [historyFinalDoc, setHistoryFinalDoc] = useState<QueryDocumentSnapshot<DocumentData, DocumentData>>();
  const [searchOccurrenceDateList, setSearchOccurrenceDateList] = useState<{ value: Date; name: string; id: string; }[]>([]);
  const [searchOccurrenceDate, setSearchOccurrenceDate] = useState<Date>(new Date());
  const listLimit = useRef<number>(50);

  const location = useLocation();
  const tranTypeLabelDefaultStyle = "text-gray-700 font-bold block mb-3";
  const tranTypeLabelStyle = "text-orange-500 font-bold block mb-3";
  const tranTypeLineDefaultStyle = "absolute bottom-0 left-0 w-full h-[2px] bg-gray-300";
  const tranTypeLineStyle = "absolute bottom-0 left-0 w-full h-1 bg-orange-500";

  useEffect(() => {
    const windowBreakPoint = 1024;
    if (window.innerWidth >= windowBreakPoint) {
      // 画面幅が1024px以上の場合
      navigate("/portal/pointHistoryListWeb");
    }
  },[location.pathname, navigate]);

  useEffect(() => {
    (async () => {
      try {
        // ユーザ情報取得
        const userData = await userQuery.getUserData(currentUser?.tenantId!, currentUser?.uid!);
        // 保有ポイント数取得
        let heldPoints = 0;
        if(userData?.possessedPointMap){
          // 現在の保有ポイント数
          const keys = Object.keys(userData.possessedPointMap);
          if(keys && keys.length > 0){
            for(let i = 0; i < keys.length; i++){
              const key = Number(keys[i]);
              heldPoints = heldPoints + (userData.possessedPointMap[key].point ?? 0);
            };
          };
        };
        setOwnedPoint(heldPoints);

        let occurrenceDate = new Date();
        let occurrenceDateList: { value: Date; name: string; id: string; }[] = [];
        // 月ごとの絞り込み用のデータを作成
        for (let i = 0; i < 12; i++) {
          let name = '';
          let addMonth = 1;
          let currentDate = new Date(occurrenceDate);
          if (i === 0) {
            name = '最近の履歴（過去1ヵ月間）';
            addMonth = 2;
          } else {
            name = convertDateTimeToJapaneseStringMonth(occurrenceDate);
          }
          occurrenceDateList.push({
            value: currentDate,
            name: name,
            id: `occurrenceDate${i}`,
          });
          occurrenceDate.setDate(1);
          occurrenceDate.setMonth(occurrenceDate.getMonth() - 1);
        }
        setSearchOccurrenceDateList(occurrenceDateList);

        // ポイント履歴の件数を取得
        const historyGrantLength = await pointDataQuery.getUserPointHistoryGrantLength(currentUser?.tenantId!, currentUser?.uid!, searchOccurrenceDate);
        const historyUsageLength = await pointDataQuery.getUserPointHistoryUsageLength(currentUser?.tenantId!, currentUser?.uid!, searchOccurrenceDate);
        setPointHistoryGrantLength(historyGrantLength);
        setPointHistoryUsageLength(historyUsageLength);

        // ポイント履歴データ取得
        const tranType = isGrant ? '付与' : '利用';
        const pointHistoryList = await pointDataQuery.getUserPointHistoryListSort(currentUser?.tenantId!, currentUser?.uid!, tranType, searchOccurrenceDate, listLimit.current, historyFinalDoc);
        const convertHistories = pointHistoryList.history.map((item: PointHistoryData) => {
          return pointDataTimestampToDate(item);
        })
        setPointHistoryList(convertHistories);
        setHistoryFinalDoc(pointHistoryList.finalDoc);
      } catch(err: any) {
        console.error(err)
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_GET_DATA, "danger")));
      }
    })();
  },[]);

  useEffect(() => {
    rowListDataSet(pointHistoryList);
  },[pointHistoryList]);

  async function rowListDataSet(historyData: any) {
    const rowData:any = [];

    await Promise.all(historyData.map(async (history: PointHistoryData) => {
      rowData.push({
        ptName: history.ptName,
        grantPoints: history.grantPoints,
        occurrenceDate: convertDateTimeToJapaneseStringHalfwidth(history.occurrenceDate),
        grantFlg: history.grantFlg,
        tranType: history.tranType,
        menu: (
          <Button
            onClick={() => navigate(`/portal/pointHistoryListMobile/${history.id}/info`)}
            className={"h-10 text-sm items-center whitespace-pre w-[55px] h-[50px]"}
            color={"darkOrange"}
            children="読む" />
        )
      });
    }));
    setRowList(rowData);
    setIsLoad(false);
  }

  const onClickTranType = async (isTranTypeGrant: boolean) => {
    setIsLoad(true);
    try {
      setIsGrant(isTranTypeGrant);
      setHistoryFinalDoc(undefined);
      setSearchOccurrenceDate(new Date());
      const occurrenceDate = new Date();

      // ポイント履歴の件数を取得
      const historyGrantLength = await pointDataQuery.getUserPointHistoryGrantLength(currentUser?.tenantId!, currentUser?.uid!, occurrenceDate);
      const historyUsageLength = await pointDataQuery.getUserPointHistoryUsageLength(currentUser?.tenantId!, currentUser?.uid!, occurrenceDate);
      setPointHistoryGrantLength(historyGrantLength);
      setPointHistoryUsageLength(historyUsageLength);

      const tranType = isTranTypeGrant ? '付与' : '利用';
      const pointHistoryList = await pointDataQuery.getUserPointHistoryListSort(currentUser?.tenantId!, currentUser?.uid!, tranType, occurrenceDate, listLimit.current, undefined);
      const convertHistories = pointHistoryList.history.map((item: PointHistoryData) => {
        return pointDataTimestampToDate(item);
      })
      setPointHistoryList(convertHistories);
      setHistoryFinalDoc(pointHistoryList.finalDoc);
    } catch (err: any) {
      console.error(err)
      dispatch(alertOpen(AlertValue(ConstMessage.ERR_GET_DATA, "danger")));
    }
  }

  // 履歴を追加取得
  const onClickMoreHistory = async () => {
    setIsLoad(true);
    try {
      const tranType = isGrant ? '付与' : '利用';
      const pointHistory = await pointDataQuery.getUserPointHistoryListSort(currentUser?.tenantId!, currentUser?.uid!, tranType, searchOccurrenceDate, listLimit.current, historyFinalDoc);
      const historyList = [...new Set(pointHistoryList)];
      const convertHistories = pointHistory.history.map((item: PointHistoryData) => {
        return pointDataTimestampToDate(item);
      })
      historyList.push(...new Set(convertHistories));
      setPointHistoryList(historyList);
      setHistoryFinalDoc(pointHistory.finalDoc);
    } catch (err: any) {
      console.error(err)
      dispatch(alertOpen(AlertValue(ConstMessage.ERR_GET_DATA, "danger")));
    }
  }

  // 検索
  const searchDate = async (date: Date) => {
    setIsLoad(true);
    try {
      const occurrenceDate = new Date(date);
      setSearchOccurrenceDate(occurrenceDate);

      // ポイント履歴の件数を取得
      const historyGrantLength = await pointDataQuery.getUserPointHistoryGrantLength(currentUser?.tenantId!, currentUser?.uid!, occurrenceDate);
      const historyUsageLength = await pointDataQuery.getUserPointHistoryUsageLength(currentUser?.tenantId!, currentUser?.uid!, occurrenceDate);
      setPointHistoryGrantLength(historyGrantLength);
      setPointHistoryUsageLength(historyUsageLength);

      setHistoryFinalDoc(undefined);
      const tranType = isGrant ? '付与' : '利用';
      const pointHistoryList = await pointDataQuery.getUserPointHistoryListSort(currentUser?.tenantId!, currentUser?.uid!, tranType, occurrenceDate, listLimit.current, undefined);
      const convertHistories = pointHistoryList.history.map((item: PointHistoryData) => {
        return pointDataTimestampToDate(item);
      })
      setPointHistoryList(convertHistories);
      setHistoryFinalDoc(pointHistoryList.finalDoc);
    } catch (err: any) {
      console.error(err)
      dispatch(alertOpen(AlertValue(ConstMessage.ERR_GET_DATA, "danger")));
    }
  }

  return (
    <div className="h-full w-full">
      <nav className="flex justify-end items-start pt-3 pr-1" aria-label="Breadcrumb">
        {/* <button
          className="flex px-4 justify-center font-medium rounded-md shadow transition ease-in duration-200 rounded h-10 px-2 text-sm items-center text-orange-500 bg-white border-2 border-orange-500 hover:bg-orange-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 focus:ring-offset-orange-100"
          onClick={() => {}}
        >
          <span>ポイント交換</span>
        </button> */}
      </nav>
      <div className="flex items-center justify-center py-3 px-1 mx-2">
        <h2 className="text-2xl font-semibold text-gray-900">
          ポイント履歴
        </h2>
      </div>
      <div className="max-w-5xl px-4">
        <div className="flex flex-col">
          <div className="w-full max-w-[300px]">
            <div className="text-gray-900">現在の保有ポイント数</div>
            <div className="flex items-center space-x-10 mt-1 justify-end">
              <span className="text-xl font-bold"><span className="text-3xl text-orange-500">{ownedPoint.toLocaleString()}</span> pt</span>
              <button
                type="button"
                className="rounded-md shadow transition ease-in duration-200 h-10 w-20 md:w-24 px-1 md:px-3 text-sm font-medium items-center hover:bg-orange-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 focus:ring-offset-orange-100 text-orange-500 bg-white border-2 border-orange-500"
                onClick={() => navigate(`/portal/pointHistoryListMobile/heldPointInfo`)}
              >
                詳細
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="pt-4 px-1 flex justify-between items-center w-full border-b border-gray-300">
        <div className="relative w-1/2 text-center" onClick={() => onClickTranType(true)}>
          <span className={isGrant ? tranTypeLabelStyle : tranTypeLabelDefaultStyle}>獲得</span>
          <div className={isGrant ? tranTypeLineStyle : tranTypeLineDefaultStyle}></div>
        </div>
        <div className="relative w-1/2 text-center" onClick={() => onClickTranType(false)}>
          <span className={!isGrant ? tranTypeLabelStyle : tranTypeLabelDefaultStyle}>利用</span>
          <div className={!isGrant ? tranTypeLineStyle : tranTypeLineDefaultStyle}></div>
        </div>
      </div>
      <div className={'px-4'}>
        <SelectFields
          id="date"
          color="indigo"
          value={searchOccurrenceDate}
          className={"h-10 w-60"}
          selectItems={searchOccurrenceDateList}
          onChange={(e:any) => searchDate(e.target.value)}
        />
      </div>
      <div className={'px-4 pb-2'}>
        {
          rowList.map((item) => {return (
            <>
              <div className="flex justify-between border-b py-2">
                <div className="flex flex-col w-full">
                  <div className="flex">
                    <span className="w-2/3 sm:w-4/5">
                      <p>{item.ptName}</p>
                    </span>
                    <span className="w-1/3 sm:w-1/5 font-semibold mr-1" style={{textAlign: 'right'}}><span className="text-orange-500 text-xl">{item.tranType === '利用' ? Math.abs(item.grantPoints).toLocaleString() : item.grantPoints.toLocaleString()}</span>{' pt'}</span>
                  </div>
                  <div className="flex text-gray-500 mb-1 ml-4">
                    {item.occurrenceDate}
                  </div>
                  {
                    item.tranType === '付与' || item.tranType === '調整' ? 
                      item.grantFlg ?
                        <div className="flex justify-center items-center relative text-white text-sm border-green-500 bg-green-500 border-[1px] border-solid border-r-0 h-6 w-fit px-2 md:px-4">
                          <span className="absolute bg-green-500 border-green-500 border-t-[1px] border-r-[1px] right-[-0.5rem] h-[1rem] w-[1rem] transform rotate-45" />
                          {'付与完了'}
                        </div>
                      : 
                        <div className="flex justify-center items-center relative text-white text-sm border-orange-500 bg-orange-500 border-[1px] border-solid border-r-0 h-6 w-[90px] px-2 md:px-4">
                          <span className="absolute bg-orange-500 border-orange-500 border-t-[1px] border-r-[1px] right-[-0.5rem] h-[1rem] w-[1rem] transform rotate-45" />
                          {'付与処理中'}
                        </div>
                    :
                      item.tranType === '失効' ?
                        <div className="flex justify-center items-center relative text-white text-sm border-gray-500 bg-gray-500 border-[1px] border-solid border-r-0 h-6 w-[50px] px-2 md:px-4">
                          <span className="absolute bg-gray-500 border-gray-500 border-t-[1px] border-r-[1px] right-[-0.5rem] h-[1rem] w-[1rem] transform rotate-45" />
                          {'失効'}
                        </div>
                      :
                        <></>
                  }
                </div>
                <div className="flex items-center ml-1">
                  {item.menu}
                </div>
              </div>
            </>
          )})
        }
        <div className={"my-3 flex justify-center"}>
          {isLoad ? 
            <div className="py-1"><Spiner /></div>
          :
            (rowList.length > 0 && (isGrant ? pointHistoryGrantLength : pointHistoryUsageLength) > rowList.length) ? 
              <Button
                onClick={() => onClickMoreHistory()}
                className={"h-12 w-2/3 items-center font-bold"}
                color={"darkOrange"}
                children="もっと見る" />
            :
             <></>
          }
        </div>
      </div>
    </div>
  )
}